import { useState } from "react";
import { LogOut, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DangerZoneSectionProps {
  onClose: () => void;
}

export function DangerZoneSection({ onClose }: DangerZoneSectionProps) {
  const { user, signOut, isAuthenticated } = useAuth();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    onClose();
    toast.success("Signed out successfully");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setDeleting(true);
    try {
      // Delete user data from profiles table
      if (user) {
        await supabase
          .from('profiles')
          .delete()
          .eq('user_id', user.id);
        
        // Delete round history
        await supabase
          .from('round_history')
          .delete()
          .eq('user_id', user.id);
        
        // Delete achievements
        await supabase
          .from('user_achievements')
          .delete()
          .eq('user_id', user.id);

        // Delete golf groups
        await supabase
          .from('golf_groups')
          .delete()
          .eq('user_id', user.id);

        // Get current session token
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (accessToken) {
          // Call edge function to delete auth record
          const { error: deleteAuthError } = await supabase.functions.invoke('delete-user-account', {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          });

          if (deleteAuthError) {
            console.error('Error deleting auth account:', deleteAuthError);
            // Still sign out even if auth deletion fails
            await signOut();
            toast.error("Your data was deleted but there was an issue removing your login. Please contact support if you can still sign in.");
            onClose();
            return;
          }
        }
      }

      // Sign out (session will be invalid anyway after auth deletion)
      await signOut();
      
      toast.success("Your account has been completely deleted.");
      onClose();
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error("Failed to delete account. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Sign Out Button */}
      <Button
        variant="outline"
        className="w-full"
        onClick={handleSignOut}
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>

      {/* Delete Account */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="destructive"
            className="w-full"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Account
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This action cannot be undone. This will permanently delete your account data including:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Your profile and account name</li>
                <li>All round history and statistics</li>
                <li>Your achievements</li>
                <li>Saved golf groups</li>
              </ul>
              <div className="pt-2">
                <Label htmlFor="delete-confirm" className="text-foreground">
                  Type <span className="font-bold">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value.toUpperCase())}
                  placeholder="Type DELETE"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmation("")}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== "DELETE" || deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
